import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { StatCard } from '../components/StatCard';
import { Badge } from '../components/Badge';
import { formatDate } from '../utils/format';
import { EmptyState } from '../components/EmptyState';

const TABS = /** @type {const} */ (['onboarding', 'offboarding', 'timeline']);

const ONBOARD_CHECK_KEYS = [
  'personal',
  'idDoc',
  'bank',
  'contract',
  'laborReg',
  'equipment',
  'access',
  'induction',
  'mandatoryTraining',
  'managerMeet',
];

function statusVariant(s) {
  if (s === 'completed') return 'approved';
  if (s === 'blocked') return 'rejected';
  if (s === 'in_progress') return 'pending';
  return 'draft';
}

export function EmployeeLifecycle() {
  const { t } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'onboarding';
  const [cases, setCases] = useState([]);
  const [loadErr, setLoadErr] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get('/api/lifecycle/cases')
      .then((r) => {
        setCases(Array.isArray(r.data) ? r.data : []);
        setLoadErr(false);
      })
      .catch(() => {
        setCases([]);
        setLoadErr(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onboarding = useMemo(() => cases.filter((c) => c.case_type === 'onboarding'), [cases]);
  const offboarding = useMemo(() => cases.filter((c) => c.case_type === 'offboarding'), [cases]);

  const stats = useMemo(() => {
    const onboardActive = onboarding.filter((x) => x.status !== 'completed').length;
    const offActive = offboarding.filter((x) => x.status !== 'completed').length;
    const all = [...onboarding, ...offboarding];
    const avg =
      all.length > 0 ? Math.round(all.reduce((a, x) => a + (x.progress || 0), 0) / all.length) : 0;
    return { onboardActive, offActive, avg };
  }, [onboarding, offboarding]);

  const setTab = (id) => setSearchParams({ tab: id });

  const toggleCheck = async (caseId, key) => {
    const row = cases.find((c) => c.id === caseId);
    if (!row) return;
    const checklist = { ...(row.checklist || {}), [key]: !row.checklist?.[key] };
    try {
      const { data } = await api.patch(`/api/lifecycle/cases/${caseId}`, { checklist });
      setCases((prev) => prev.map((c) => (c.id === caseId ? data : c)));
    } catch {
      load();
    }
  };

  return (
    <div className="page-stack lifecycle-page">

      <div className="grid-stats">
        <StatCard icon="↻" label={t('lifecycle.statOnboardActive')} value={String(stats.onboardActive)} />
        <StatCard icon="◇" label={t('lifecycle.statOffboardActive')} value={String(stats.offActive)} />
        <StatCard icon="◎" label={t('lifecycle.statAvgProgress')} value={`${stats.avg}%`} />
      </div>

      <div className="tab-row">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            className={tab === id ? 'tab-btn is-active' : 'tab-btn'}
            onClick={() => setTab(id)}
          >
            {t(`lifecycle.tab_${id}`)}
          </button>
        ))}
      </div>

      {loading ? <p className="panel__muted">{t('lifecycle.loading')}</p> : null}
      {loadErr ? <p className="panel__muted text-sm">{t('lifecycle.loadError')}</p> : null}

      {tab === 'onboarding' && !loading && (
        <section className="panel panel--elevated">
          <h2 className="panel__title">{t('lifecycle.onboardTitle')}</h2>
          {onboarding.length === 0 ? (
            <EmptyState title={t('lifecycle.emptyOnboard')} />
          ) : (
            <ul className="lifecycle-list">
              {onboarding.map((row) => (
                <li key={row.id} className="lifecycle-card panel">
                  <div className="lifecycle-card__head">
                    <strong>{row.employee_name}</strong>
                    <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                    <span className="panel__muted text-sm">{row.progress}%</span>
                  </div>
                  <p className="panel__muted text-sm">
                    {row.start_date ? formatDate(row.start_date) : '—'} · {row.manager_name || '—'}
                  </p>
                  <div className="lifecycle-checks">
                    {ONBOARD_CHECK_KEYS.map((k) => (
                      <label key={k} className="lifecycle-check">
                        <input
                          type="checkbox"
                          checked={!!row.checklist?.[k]}
                          onChange={() => toggleCheck(row.id, k)}
                        />
                        {t(`lifecycle.checkOnboard_${k}`)}
                      </label>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'offboarding' && !loading && (
        <section className="panel panel--elevated">
          <h2 className="panel__title">{t('lifecycle.offboardTitle')}</h2>
          {offboarding.length === 0 ? (
            <EmptyState title={t('lifecycle.emptyOffboard')} />
          ) : (
            <ul className="lifecycle-list">
              {offboarding.map((row) => (
                <li key={row.id} className="lifecycle-card panel">
                  <strong>{row.employee_name}</strong>
                  <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                  <span className="panel__muted text-sm">{row.progress}%</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'timeline' && !loading && (
        <section className="panel panel--elevated">
          <h2 className="panel__title">{t('lifecycle.timelineTitle')}</h2>
          {cases.length === 0 ? (
            <EmptyState title={t('lifecycle.emptyTimeline')} />
          ) : (
            <ul className="lifecycle-timeline">
              {[...cases]
                .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                .map((ev) => (
                  <li key={ev.id}>
                    <span className="panel__muted text-sm">{formatDate(ev.updated_at?.slice?.(0, 10) || ev.updated_at)}</span>
                    <strong>{ev.employee_name}</strong> — {ev.case_type} ({ev.status})
                  </li>
                ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
